import { useMemo } from "react";
import * as Yup from "yup";

import { DealMetricType } from "@vega-api/generated/types/dealMetricType";
import { coreCurrencies, type CoreCurrency } from "@vega-core/models/money";
import { m } from "@vega-core/paraglide/messages";

import {
  FORM_FIELD_NAMES,
  MODAL_OPTION_VALUES,
} from "../constants/modalConstants";

type ModalType = "metric" | "section";

type MoneyValue = {
  amount: string;
  currency: CoreCurrency;
};

const isMoneyValue = (value: unknown): value is MoneyValue => {
  if (
    typeof value !== "object" ||
    value === null ||
    !("amount" in value) ||
    !("currency" in value)
  ) {
    return false;
  }

  return (
    typeof value.amount === "string" &&
    typeof value.currency === "string" &&
    coreCurrencies.includes(value.currency as CoreCurrency)
  );
};

export const useModalValidation = (
  modalType: ModalType,
  existingNames: { name: string; metricType?: string }[],
) => {
  const validationSchema = useMemo(
    () =>
      Yup.object({
        [FORM_FIELD_NAMES.SELECTED_OPTION]: Yup.string().required(
          m.coInvestments_lpDealOverviewContent_pleaseSelectOption(),
        ),
        [FORM_FIELD_NAMES.METRIC_TYPE]: Yup.string().nullable(),
        [FORM_FIELD_NAMES.CUSTOM_LABEL]: Yup.string().when(
          FORM_FIELD_NAMES.SELECTED_OPTION,
          {
            is: MODAL_OPTION_VALUES.CUSTOM,
            then: (schema) =>
              schema
                .required(m.coInvestments_lpDealOverviewContent_labelRequired())
                .test(
                  "is-unique",
                  m.coInvestments_lpDealOverviewContent_nameAlreadyExists({
                    type: modalType,
                  }),
                  function (value) {
                    if (!value) return true;

                    return !existingNames.some(
                      (item) => item.name.toLowerCase() === value.toLowerCase(),
                    );
                  },
                ),
            otherwise: (schema) => schema.notRequired(),
          },
        ),
        [FORM_FIELD_NAMES.CUSTOM_VALUE]: Yup.mixed().test(
          "value-required",
          m.coInvestments_lpDealOverviewContent_valueRequired(),
          function (value) {
            const metricType = this.parent[FORM_FIELD_NAMES.METRIC_TYPE];

            if (metricType === DealMetricType.MONEY) {
              if (!isMoneyValue(value)) {
                return false;
              }

              return value.amount.trim() !== "";
            }

            return typeof value === "string" && value.trim() !== "";
          },
        ),
      }),
    [modalType, existingNames],
  );

  return { validationSchema };
};
